import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders.js';

const base = (projectId) => `${config.bffUrl}/api/mia-platform/projects/${encodeURIComponent(projectId)}/publish`;

// The server's release gate (vfs-service, through the BFF): 409 `{ code: 'quality_gate_blocked', blocking }` while
// Quality blocks Publish. The error carries `code` and `blocking` so the dialog shows its gate banner.
export const GATE_BLOCKED = 'quality_gate_blocked';

async function readJson(response, fallback) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.success === false) {
    const error = new Error(body.error || fallback);
    error.details = body.details;
    error.repoUrl = body.repoUrl;
    if (body.code) { error.code = body.code; }
    if (Array.isArray(body.blocking)) { error.blocking = body.blocking; }
    error.status = response.status;
    throw error;
  }
  return body;
}

// What would be published, what is missing, and which destinations the user can use.
export async function checkPublish(projectId) {
  const response = await fetch(`${base(projectId)}/check`, { headers: getBffHeaders({}, true), credentials: 'include' });
  return readJson(response, 'The project could not be checked.');
}

// Git, GitOps and the image registry go straight to vfs-service: PublishTargetsService.js (the BFF's /publish/gitlab
// had no account to push with).

// The release vfs-service remembers for a served zip (Quality's build provenance): `X-Flow-Release-Id`. Null when the
// header is missing or not readable (a cross-origin answer that does not expose it): the server then falls back to the
// project's latest pending release.
export function releaseIdOf(response) {
  try {
    return response?.headers?.get?.('X-Flow-Release-Id') || null;
  } catch {
    return null;
  }
}

// The publishable files as a zip Blob (no secrets, dependencies or build output; chat history and brain excluded).
// `onRelease(releaseId)` is called when the answer names its release.
export async function exportZip(projectId, name, { onRelease } = {}) {
  const response = await fetch(`${base(projectId)}/export.zip?name=${encodeURIComponent(name)}`, {
    headers: getBffHeaders({}, true),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('The zip could not be created.');
  }
  const releaseId = releaseIdOf(response);
  if (releaseId && onRelease) { onRelease(releaseId); }
  return response.blob();
}

// The same zip for a release (the Console delivery): refused with 409 `quality_gate_blocked` while the gate blocks.
// `onRelease(releaseId)` is called when the answer names its release (`X-Flow-Release-Id`).
export async function releaseZip(projectId, name, { onRelease } = {}) {
  const response = await fetch(`${base(projectId)}/release.zip?name=${encodeURIComponent(name)}`, {
    headers: getBffHeaders({}, true),
    credentials: 'include'
  });
  if (!response.ok) {
    await readJson(response, 'The project could not be packed for the release.');
  }
  const releaseId = releaseIdOf(response);
  if (releaseId && onRelease) { onRelease(releaseId); }
  return response.blob();
}

// Downloads the publishable files as a zip (the browser needs the auth headers, so this is a fetch, not a link).
// Resolves to `{ releaseId }` (null when the answer did not name its release).
export async function downloadZip(projectId, name) {
  let releaseId = null;
  const url = URL.createObjectURL(await exportZip(projectId, name, { onRelease: (id) => { releaseId = id; } }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return { releaseId };
}
